import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaClock,
  FaRegHospital,
  FaChevronRight,
  FaEdit,
  FaUserMd,
  FaFileExcel,
} from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { useState } from "react";
import { Button, Modal } from "antd"; // Ant Design Modal
import BulkInvoiceModal from "../../Modals/BulkInvoiceModal";
const reportTypeLabel = { 0: "General", 1: "PhysioTherapy", 2: "Lymphedema" };

const ClinicCard = ({
  clinic,
  onEdit,
  onDelete,
  loading,
  selected,
  disabled,
  role,
  setClinicId,
  setBulkModalVisible,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [modalType, setModalType] = useState(""); // "doctors" or "workingDays"
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Flatten working days from all doctors
  const allWorkingDays =
    clinic.doctors?.flatMap((doc) => doc.workingDays || []) || [];
  const workingDaysCount = allWorkingDays.filter((day) => day.isWorking).length;

  // Flatten all availability hours
  const availabilityHours = allWorkingDays.reduce((total, day) => {
    return total + (day.availabilityHours?.length || 0);
  }, 0);

  const doctorsCount = clinic.doctors?.length || 0;

  const openModal = (type) => {
    setModalType(type);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setTimeout(() => setModalType(""), 300); // small delay for animation cleanup
  };

  return (
    <>
      <div
        className={`
          group relative w-full max-w-full rounded-xl border border-gray-200 overflow-hidden
          bg-white transition-all duration-300 ease-in-out transform
          md:hover:scale-[1.02]
          ${selected ? "ring-2 ring-blue-200 border-blue-300" : ""}
          ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
        `}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Top Border */}
        <div
          className={`h-1 bg-gradient-to-r from-primary to-primary/50 transition-all duration-500 ease-out ${
            isHovered ? "h-2" : "h-1"
          }`}
        />

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10 rounded-xl">
            <div className="flex items-center gap-2 text-blue-600">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <span className="text-sm font-medium">Processing...</span>
            </div>
          </div>
        )}

        <div className="p-4 sm:p-5 md:p-6 space-y-4 md:space-y-5">
          {/* Header */}
          <div className="relative">
            <div
              className={`absolute top-0 right-0 p-2 rounded-full bg-gradient-to-br from-primary to-primary/70 transition-transform ${
                isHovered
                  ? "scale-110 rotate-6 bg-gradient-to-br from-blue-100 to-blue-200"
                  : ""
              }`}
            >
              <FaRegHospital className="text-white text-base md:text-lg" />
            </div>
            <div className="pr-12 mb-1 min-w-0">
              <h3
                className={`text-lg md:text-xl font-bold text-gray-800 break-words leading-snug ${
                  isHovered ? "text-primary" : ""
                }`}
              >
                {clinic.clinicName || clinic.name}
              </h3>
              <div
                className={`h-0.5 bg-gradient-to-r from-primary to-transparent mt-1 transition-all duration-500 ${
                  isHovered ? "w-full" : "w-8"
                }`}
              />
            </div>
            <span className="inline-block text-xs mt-2 bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
              {reportTypeLabel[clinic.defaultReportType ?? 0]}
            </span>
          </div>

          {/* Contact Info */}
          {clinic.hasLocation ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="mt-0.5 p-1.5 rounded-full bg-gray-100 shrink-0">
                  <FaMapMarkerAlt className="text-gray-600 text-sm" />
                </div>
                <p className="text-sm text-gray-700 break-words min-w-0">
                  {clinic.address || clinic.detailedAddress}
                </p>
              </div>
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-1.5 rounded-full bg-gray-100 shrink-0">
                  <FaPhone className="text-gray-600 text-sm" />
                </div>
                <p className="text-sm text-gray-700 font-medium break-all min-w-0">
                  {clinic.phone || clinic.contactPhone}
                </p>
              </div>
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-1.5 rounded-full bg-gray-100 shrink-0">
                  <FaEnvelope className="text-gray-600 text-sm" />
                </div>
                <p className="text-sm text-gray-700 truncate min-w-0">
                  {clinic.email || clinic.contactEmail}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 min-h-[5rem] md:h-24 min-w-0">
              <div className="mt-0.5 p-1.5 rounded-full bg-gray-100 shrink-0">
                <FaMapMarkerAlt className="text-gray-600 text-base md:text-lg" />
              </div>
              <p className="text-base md:text-lg text-gray-700 break-words min-w-0">
                {clinic.branchName || clinic.detailedAddress}
              </p>
            </div>
          )}

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-2 md:gap-1 pt-2">
            {/* Doctors */}
            <div
              onClick={() => openModal("doctors")}
              className="cursor-pointer bg-primary/10 border-primary/30 rounded-lg p-2 md:p-1 text-center active:scale-95 transition-transform"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <FaUserMd className="text-primary text-[10px]" />
                <span className="text-[10px] sm:text-xs font-semibold text-primary uppercase">
                  Doctors
                </span>
              </div>
              <p className="font-bold text-primary">{doctorsCount}</p>
            </div>

            {/* Working Days */}
            <div
              onClick={() => openModal("workingDays")}
              className="cursor-pointer bg-orange-50 border-orange-200 rounded-lg p-2 md:p-1 text-center active:scale-95 transition-transform"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <FaCalendarAlt className="text-secondary text-[10px]" />
                <span className="text-[10px] sm:text-xs font-semibold text-secondary uppercase">
                  Days
                </span>
              </div>
              <p className="font-bold text-secondary">{workingDaysCount}</p>
            </div>

            {/* Time Slots */}
            <div className="bg-green-50 border-green-200 rounded-lg p-2 md:p-1 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <FaClock className="text-green-600 text-[10px]" />
                <span className="text-[10px] sm:text-xs font-semibold text-green-700 uppercase">
                  Slots
                </span>
              </div>
              <p className="font-bold text-green-800">{availabilityHours}</p>
            </div>
          </div>

          {role == 3 ? (
            <div className="flex gap-2 sm:gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={onEdit}
                disabled={disabled}
                className="flex-1 min-w-0 flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-primary text-white rounded-lg font-medium text-sm hover:shadow-md active:scale-95 disabled:opacity-50 hover:bg-primary_hover"
              >
                <FaEdit />
                <span>Edit</span>
                <FaChevronRight
                  className={`hidden md:block ${
                    isHovered ? "opacity-100 translate-x-0.5" : "opacity-0"
                  }`}
                />
              </button>
              <button
                onClick={onDelete}
                disabled={disabled}
                className="shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 border-red-500 border text-white rounded-lg font-medium text-sm hover:shadow-md active:scale-95 disabled:opacity-50 hover:bg-red-100"
              >
                <IoTrashOutline className="text-red-500 text-lg" />
              </button>
            </div>
          ) : (
            <Button
              icon={<FaFileExcel />}
              type="default"
              className="bg-primary text-white border-none hover:bg-primary_hover hover:!text-white focus:bg-green-700 active:bg-green-800 w-full mt-4 !h-10 whitespace-normal"
              onClick={() => {
                setClinicId(clinic.clinicId);
                setBulkModalVisible(true);
              }}
            >
              Add Excel Invoices
            </Button>
          )}
        </div>

        {selected && (
          <div className="absolute top-3 left-3">
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse shadow-lg"></div>
          </div>
        )}
      </div>

      {/* Ant Design Modal */}
      <Modal
        title={modalType === "doctors" ? "Doctors" : "Working Days"}
        open={isModalVisible}
        onCancel={closeModal}
        footer={null}
        centered
        width="min(520px, calc(100vw - 24px))"
        styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
        className="rounded-xl"
        transitionName="ant-fade"
        maskTransitionName="ant-fade"
      >
        {modalType === "doctors" && (
          <ul className="space-y-3">
            {clinic.doctors?.map((doc) => (
              <li
                key={doc.doctorId}
                className="border p-3 rounded-lg bg-gray-50"
              >
                <p className="font-semibold text-primary break-words">
                  {doc.doctorName}
                </p>
                <p className="text-sm text-gray-600 break-words">
                  {doc.specialization}
                </p>
              </li>
            ))}
          </ul>
        )}

        {modalType === "workingDays" && (
          <ul className="space-y-3">
            {allWorkingDays
              .filter((d) => d.isWorking)
              .map((day) => (
                <li
                  key={day.workingDayId}
                  className="border p-3 rounded-lg bg-gray-50"
                >
                  <p className="font-medium text-primary">
                    {
                      ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
                        day.dayOfWeek
                      ]
                    }
                  </p>
                  {day.availabilityHours?.length > 0 ? (
                    <p className="text-sm text-gray-600 break-words">
                      {day.availabilityHours
                        .map((slot) => `${slot.startTime} - ${slot.endTime}`)
                        .join(", ")}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">No time slots</p>
                  )}
                </li>
              ))}
          </ul>
        )}
      </Modal>
    </>
  );
};

export default ClinicCard;